"use client";

import { ArrowRight, User, Mail, Phone, FileText, Calendar } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function EnrollmentForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="p-8 bg-slate-800/40 border border-slate-700 rounded-lg">
        <p className="font-serif text-2xl text-white mb-4">{successMessage}</p>
        <Button onClick={resetForm} variant="outline" className="mt-2 border-slate-600 text-white hover:bg-slate-700">
          Submit another request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <FormField control={control} name="full_name" rules={{
      required: "Full name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-slate-400">
              Full Name
            </FormLabel>
            <FormControl>
              <Input placeholder="John Doe" className="border-0 border-b border-slate-700 px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-sky-500 transition-colors text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-slate-400">
              Email Address
            </FormLabel>
            <FormControl>
              <Input type="email" placeholder="john@example.com" className="border-0 border-b border-slate-700 px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-sky-500 transition-colors text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      required: "Phone number is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-slate-400">
              Phone Number
            </FormLabel>
            <FormControl>
              <Input type="tel" placeholder="(555) 123-4567" className="border-0 border-b border-slate-700 px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-sky-500 transition-colors text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="service_type" rules={{
      required: "Please select a service"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-slate-400">
              Service Required
            </FormLabel>
            <FormControl>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full border-0 border-b border-slate-700 px-0 h-11 bg-transparent focus-visible:ring-0 text-white">
                  <SelectValue placeholder="Select a service" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 text-white">
                  <SelectItem value="Respite Care">Respite Care</SelectItem>
                  <SelectItem value="Community Support">Community Support</SelectItem>
                  <SelectItem value="Individualized Support">Individualized Support</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="start_date" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-slate-400">
              Preferred Start Date
            </FormLabel>
            <FormControl>
              <Input type="date" className="border-0 border-b border-slate-700 px-0 h-11 bg-transparent focus-visible:ring-0 focus-visible:border-sky-500 transition-colors text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="additional_notes" render={({
      field
    }) => <FormItem>
            <FormLabel className="font-mono text-xs uppercase tracking-wider text-slate-400">
              Additional Notes
            </FormLabel>
            <FormControl>
              <Textarea placeholder="Please provide any specific requirements or details..." rows={4} className="border-0 border-b border-slate-700 px-0 bg-transparent focus-visible:ring-0 focus-visible:border-sky-500 transition-colors resize-none text-white" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <div className="flex items-start gap-3 pt-2">
        <Checkbox id="privacy-consent" className="mt-1 border-slate-600" />
        <label htmlFor="privacy-consent" className="text-sm text-slate-400 leading-relaxed cursor-pointer">
          I consent to Waters Well Inc. contacting me regarding my service request.
        </label>
      </div>

      {submitError && <div className="p-4 bg-red-900/20 border border-red-900/50 rounded-lg">
          <p className="text-red-400 text-sm">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} size="lg" className="w-full h-14 rounded-lg text-base font-medium bg-sky-600 hover:bg-sky-700 text-white">
        {isSubmitting ? "Submitting..." : "Submit Request"}
        <ArrowRight className="size-5 ml-1" />
      </Button>
    </Form>;
}
export default function ServiceRequest() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const steps = [{
    num: "01",
    label: "Submit Request"
  }, {
    num: "02",
    label: "Administrative Review"
  }, {
    num: "03",
    label: "Service Coordination"
  }];
  return <section data-section-id="264" id="service-request-form" className="bg-[#2D343E] py-24 md:py-32 lg:py-40">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-5 flex flex-col">
            <p className="font-mono text-xs uppercase tracking-[0.2em] text-sky-500 mb-8">
              Application / § 01
            </p>

            <h2 className="font-serif font-default text-white leading-[1.05] tracking-tight mb-8" style={{
            fontSize: "clamp(2.5rem, 5vw, 5rem)"
          }}>
              Begin Your{" "}
              <span className="italic text-slate-400 font-light">Support</span>
            </h2>

            <p className="text-slate-300 text-lg leading-relaxed mb-12 max-w-md">
              Waters Well Inc. is committed to providing high-quality, compassionate support. Complete this form to initiate your service request and our team will review your needs promptly.
            </p>

            <ul className="space-y-5 mb-16">
              {steps.map((step, i) => <li key={step.num} data-index={i} className="flex items-baseline gap-5">
                  <span className="font-mono text-sm text-sky-500 tracking-wider">
                    {step.num}
                  </span>
                  <span className="font-serif text-xl text-white">
                    {step.label}
                  </span>
                </li>)}
            </ul>

            <div className="mt-auto hidden lg:block opacity-60">
              <svg width="120" height="40" viewBox="0 0 120 40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <path d="M2 30 Q 15 10, 30 20 T 60 20 T 90 20 T 118 20" stroke="#3A6073" strokeWidth="1.5" fill="none" strokeLinecap="round" />
                <circle cx="30" cy="20" r="3" fill="#0099CC" />
                <circle cx="60" cy="20" r="3" fill="#0099CC" />
                <circle cx="90" cy="20" r="3" fill="#0099CC" />
                <line x1="33" y1="20" x2="33" y2="4" stroke="#3A6073" strokeWidth="1.5" />
                <line x1="63" y1="20" x2="63" y2="4" stroke="#3A6073" strokeWidth="1.5" />
                <line x1="93" y1="20" x2="93" y2="4" stroke="#3A6073" strokeWidth="1.5" />
              </svg>
            </div>
          </div>

          <div className="lg:col-span-7">
            <div className="bg-[#3A6073] border border-slate-600 rounded-lg shadow-lg p-8 md:p-12">
              <FormProvider formKey="service_request_form_get_in_touch" formId="service_request_form_get_in_touch" sectionName="service-request-form" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              service_type: "",
              start_date: "",
              additional_notes: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true,
                pattern: "phone"
              },
              service_type: {
                required: true
              }
            }} submitText="Submit Request" successMessage="Thank you for your request. A member of our team will contact you shortly." errorMessage="An error occurred. Please try again or contact us directly.">
                <EnrollmentForm />
              </FormProvider>
            </div>
          </div>
        </div>
      </div>
    </section>;
}